import { createContext, useEffect, useMemo, useState } from "react";
import { createTheme } from "@mui/material/styles";
import { PaletteMode } from "@mui/material";

import { getImages } from "../images/getImages";
import { IUniversalNumber, AppTheme } from "./types";
import { COLOR_MATRIX } from "./colorMatrix";
import { getItemFromLS, setItemToLS } from "@/shared/utils/localStorageHelpers";
import { StorageKeys } from "@/shared/types";

export const getMatrixByMode = (mode: PaletteMode): IUniversalNumber => ({
  ...(mode === "dark" ? COLOR_MATRIX.DARK : COLOR_MATRIX.LIGHT),
});

export const themeSettings = (mode: PaletteMode): AppTheme => {
  const matrix = getMatrixByMode(mode);

  return {
    palette: {
      mode,
    },
    rgb: matrix.rgb,
    colors: matrix.colors,
    images: getImages(mode),
    boxShadows: matrix.boxShadows,
    coefficient: 1,
    scaleFontSize: 1,
  };
};

export const ColorModeContext = createContext<{ toggleColorMode: () => void }>({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const storedMode = getItemFromLS<PaletteMode>(StorageKeys.THEME_MODE);
    return storedMode === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    setItemToLS(StorageKeys.THEME_MODE, mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );
  const theme = useMemo(() => {
    const newTheme = createTheme(themeSettings(mode));
    return newTheme;
  }, [mode]);
  return { theme, colorMode };
};
